import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Zap, Leaf, ArrowRight, Truck, Settings, Package, Wrench, Paintbrush, Shield, Building2, HardHat, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import heroVideo1 from "@/assets/hero-video.mp4";
import heroVideo2 from "@/assets/hero-video-2.mp4";
import heroVideo3 from "@/assets/hero-video-3.mp4";
import heroVideo4 from "@/assets/hero-video-4.mov";

interface HeroSlide {
  video: string;
  badge: string;
  isArabic?: boolean;
  title: string;
  titleHighlight: string;
  subtitle: string;
  description: string;
  features: { icon: typeof Zap; label: string; color: string }[];
  primaryCta: { label: string; href: string };
  secondaryCta: { label: string; href: string };
  accentColor: "primary" | "eco-green" | "sky-blue" | "purple";
}

const heroSlides: HeroSlide[] = [
  {
    video: heroVideo1,
    badge: "Sustainable Development LLC — تطوير الاستدامة",
    title: "House Of",
    titleHighlight: "Mobility",
    subtitle: "E-Transportation",
    description: "We chose sustainability because we operate in an eco-conscious manner and believe that every strategy, product, and service can leave a green footprint. Our sustainable mobility solutions are aligned with Vision 2030.",
    features: [
      { icon: Zap, label: "Smart", color: "text-sky-blue" },
      { icon: Clock, label: "Fast", color: "text-purple" },
      { icon: Shield, label: "Reliable", color: "text-amber-glow" },
      { icon: Leaf, label: "Green", color: "text-eco-green" },
    ],
    primaryCta: { label: "Explore Services", href: "/services" },
    secondaryCta: { label: "Contact Us", href: "/contact" },
    accentColor: "primary",
  },
  {
    video: heroVideo2,
    badge: "LEMO — Event & Fleet Mobility",
    title: "LEMO",
    titleHighlight: "Fleet Rentals",
    subtitle: "Event & Fleet Mobility Rentals",
    description: "Electric fleets delivered, managed, and collected for your corporate events, government operations, and large-scale programs. Scalable mobility — zero emissions, zero hassle.",
    features: [
      { icon: Truck, label: "Delivered", color: "text-eco-green" },
      { icon: Settings, label: "Managed", color: "text-sky-blue" },
      { icon: Package, label: "Collected", color: "text-purple" },
      { icon: Leaf, label: "Green", color: "text-eco-green" },
    ],
    primaryCta: { label: "Explore LEMO", href: "/lemo" },
    secondaryCta: { label: "Request a Fleet", href: "/lemo#booking" },
    accentColor: "eco-green",
  },
  {
    video: heroVideo3,
    badge: "Fleet Customization & Modification",
    title: "Custom-Built",
    titleHighlight: "Fleet Solutions",
    subtitle: "Tailored to Your Brand & Operations",
    description: "From government-spec patrol vehicles to branded corporate fleets — we engineer, modify, and deliver vehicles configured to your exact operational requirements. OEM-grade builds at scale.",
    features: [
      { icon: Wrench, label: "Engineered", color: "text-sky-blue" },
      { icon: Paintbrush, label: "Branded", color: "text-purple" },
      { icon: Shield, label: "Certified", color: "text-amber-glow" },
      { icon: Building2, label: "At Scale", color: "text-eco-green" },
    ],
    primaryCta: { label: "Explore Customization", href: "/product-customization" },
    secondaryCta: { label: "Request a Quote", href: "/contact" },
    accentColor: "sky-blue",
  },
  {
    video: heroVideo4,
    badge: "Mega Projects — Run & Operate",
    title: "Mega Project",
    titleHighlight: "Operations",
    subtitle: "End-to-End Fleet Run & Operate",
    description: "We deploy, staff, and operate entire mobility fleets for mega events, smart cities, and national programs. From Hajj crowd management to Formula 1 logistics — we run it so you don't have to.",
    features: [
      { icon: HardHat, label: "Operated", color: "text-amber-glow" },
      { icon: Building2, label: "Enterprise", color: "text-sky-blue" },
      { icon: Truck, label: "Deployed", color: "text-eco-green" },
      { icon: Shield, label: "Turnkey", color: "text-purple" },
    ],
    primaryCta: { label: "View Mega Projects", href: "/projects" },
    secondaryCta: { label: "Partner With Us", href: "/contact" },
    accentColor: "purple",
  },
];

const gradientMap = {
  "primary": "bg-gradient-to-r from-primary via-amber-glow to-primary",
  "eco-green": "bg-gradient-to-r from-eco-green via-teal-bright to-eco-green-light",
  "sky-blue": "bg-gradient-to-r from-sky-blue via-sky-blue-light to-sky-blue",
  "purple": "bg-gradient-to-r from-purple via-rose to-purple",
};

const badgeMap = {
  "primary": "bg-gradient-to-r from-primary/20 to-amber-glow/20 text-primary border-primary/20",
  "eco-green": "bg-gradient-to-r from-eco-green/20 to-teal-bright/20 text-eco-green border-eco-green/30",
  "sky-blue": "bg-gradient-to-r from-sky-blue/20 to-sky-blue-light/20 text-sky-blue border-sky-blue/30",
  "purple": "bg-gradient-to-r from-purple/20 to-rose/20 text-purple border-purple/30",
};

const btnMap = {
  "primary": "bg-gradient-to-r from-eco-green via-teal-bright to-eco-green-light shadow-eco-green/25 hover:shadow-eco-green/30",
  "eco-green": "bg-gradient-to-r from-eco-green via-teal-bright to-eco-green-light shadow-eco-green/25 hover:shadow-eco-green/30",
  "sky-blue": "bg-gradient-to-r from-sky-blue via-sky-blue-light to-sky-blue shadow-sky-blue/25 hover:shadow-sky-blue/30",
  "purple": "bg-gradient-to-r from-purple via-rose to-purple shadow-purple/25 hover:shadow-purple/30",
};

const Hero = () => {
  const [videoIndex, setVideoIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const [contentFade, setContentFade] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  const currentSlide = heroSlides[videoIndex];

  const handleVideoEnd = useCallback(() => {
    setFadeIn(false);
    setContentFade(false);
    setTimeout(() => {
      setVideoIndex((prev) => (prev + 1) % heroSlides.length);
      setFadeIn(true);
      setContentFade(true);
    }, 600);
  }, []);

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Video */}
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          key={videoIndex}
          autoPlay
          muted
          playsInline
          onEnded={handleVideoEnd}
          className={`w-full h-full object-cover transition-opacity duration-700 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}
        >
          <source src={heroSlides[videoIndex].video} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/70 to-background" />
      </div>

      {/* Animated gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-eco-green/5 via-transparent to-sky-blue/5 animate-gradient-shift" style={{ backgroundSize: "200% 200%" }} />

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-2 h-2 rounded-full ${i % 2 === 0 ? 'bg-eco-green/30' : 'bg-sky-blue/30'}`}
            style={{
              left: `${10 + i * 12}%`,
              top: `${20 + (i % 3) * 25}%`,
              animation: `float ${3 + i * 0.5}s ease-in-out infinite`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 pt-24">
        <div className={`max-w-3xl transition-all duration-500 ${contentFade ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {/* Badge */}
          <div className="inline-block mb-6">
            <span className={`px-4 py-2 rounded-full text-sm font-semibold border inline-block ${badgeMap[currentSlide.accentColor]}`}>
              {currentSlide.badge}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-5xl md:text-7xl font-bold mb-4">
            {currentSlide.title}{" "}
            <span className={`bg-clip-text text-transparent ${gradientMap[currentSlide.accentColor]}`}>
              {currentSlide.titleHighlight}
            </span>
          </h1>

          {/* Features */}
          <div className="flex flex-wrap gap-4 mb-8">
            {currentSlide.features.map((feature, index) => (
              <div key={feature.label} className="flex items-center gap-2 text-muted-foreground group">
                <div className="p-1.5 rounded-lg bg-secondary/50 group-hover:scale-110 transition-transform">
                  <feature.icon className={`w-5 h-5 ${feature.color}`} />
                </div>
                <span className="font-medium">{feature.label}</span>
                {index < currentSlide.features.length - 1 && (
                  <span className="ml-2 text-muted-foreground/50">•</span>
                )}
              </div>
            ))}
          </div>

          {/* Subtitle */}
          <h2 className="text-2xl md:text-3xl font-semibold mb-4">
            {currentSlide.subtitle}
          </h2>

          {/* Description */}
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl leading-relaxed">
            {currentSlide.description}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-3 md:gap-4">
            <Button
              size="lg"
              asChild
              className={`font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 text-white text-sm md:text-base px-5 md:px-8 h-10 md:h-12 ${btnMap[currentSlide.accentColor]}`}
            >
              <Link to={currentSlide.primaryCta.href}>
                {currentSlide.primaryCta.label}
                <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-2" />
              </Link>
            </Button>
            <Button variant="heroOutline" size="lg" asChild className="text-sm md:text-base px-5 md:px-8 h-10 md:h-12">
              <Link to={currentSlide.secondaryCta.href}>
                {currentSlide.secondaryCta.label}
              </Link>
            </Button>
          </div>

          {/* Slide indicators */}
          <div className="flex gap-2 mt-10">
            {heroSlides.map((_, i) => (
              <div
                key={i}
                className={`h-1 rounded-full transition-all duration-500 ${
                  i === videoIndex
                    ? `w-10 ${i === 0 ? 'bg-primary' : i === 1 ? 'bg-eco-green' : i === 2 ? 'bg-sky-blue' : 'bg-purple'}`
                    : 'w-4 bg-muted-foreground/30'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <div className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-gradient-to-b from-eco-green to-primary rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
